import { useTranslate } from 'core/hooks/useTranslate';
import { useCallback } from 'react';
import type { ListSimpleDropdownItem } from '../../../../../molecules/ListSimpleDropdown/types';
import type { PopoverTriggerProps } from '../../../../../molecules/Popover/types';
import { usePopover } from '../../../../../molecules/Popover/usePopover';
import { DIALOG_TRAP_ROOT_SELECTOR } from '../../../../../organisms/dialogs/Dialog/useDialog';

/**
 * Ids de las opciones del menú. Se usan tanto para construir los items como
 * para despachar el handler correspondiente en `onOptionClick`.
 */
const ADD_NEW_OPTION_ID = 'add-new';
const COPY_MODULE_OPTION_ID = 'copy-module';

type UseAddModuleMenuButtonParams = {
  onAddNew: () => void;
  onCopyModule: () => void;
};

type UseAddModuleMenuButtonReturn = {
  anchorEl: HTMLElement | null;
  dismiss: () => void;
  getTriggerProps: () => PopoverTriggerProps;
  isOpen: boolean;
  items: ListSimpleDropdownItem[];
  onOptionClick: (item: ListSimpleDropdownItem) => void;
  panelContainer: HTMLElement | undefined;
};

/**
 * Lógica del `AddModuleMenuButton`. Concentra traducción, estado del `Popover`
 * (`usePopover`) y handlers, dejando el componente como JSX puro.
 *
 * Dos cierres distintos (patrón WAI-ARIA APG, ver `usePopover`):
 *  - `close()` al activar una opción: devuelve el foco al trigger.
 *  - `dismiss()` en click-away/Escape: no mueve el foco (el Escape lo devuelve
 *    por su cuenta desde el panel).
 */
function useAddModuleMenuButton({ onAddNew, onCopyModule }: UseAddModuleMenuButtonParams): UseAddModuleMenuButtonReturn {
  const { t } = useTranslate();
  const { anchorEl, close, dismiss, getTriggerProps, isOpen } = usePopover({ hasPopup: 'menu' });

  // Sin `useMemo`: `useTranslate` devuelve una `t` nueva en cada render, así que
  // memoizar por `[t]` recalcularía igualmente y solo añadiría ruido.
  const items: ListSimpleDropdownItem[] = [
    { icon: 'add', id: ADD_NEW_OPTION_ID, name: t('organize-content:Add new') },
    { icon: 'copy-outline', id: COPY_MODULE_OPTION_ID, name: t('organize-content:Copy module from another program') },
  ];

  const onOptionClick = useCallback(
    (item: ListSimpleDropdownItem): void => {
      close();
      if (item.id === ADD_NEW_OPTION_ID) {
        onAddNew();
        return;
      }
      if (item.id === COPY_MODULE_OPTION_ID) {
        onCopyModule();
      }
    },
    [close, onAddNew, onCopyModule]
  );

  // El panel va a la raíz del trap del diálogo: dentro de él sigue alcanzable por
  // teclado y queda por encima de los `overflow` de `.dialog-organize-course__content`
  // y `.evaluation-period-section__content`, que lo recortarían en línea. Fuera de
  // un diálogo (Storybook, tests aislados) `undefined` deja el default del `Popper`
  // (portal a `body`).
  const panelContainer = anchorEl?.closest<HTMLElement>(DIALOG_TRAP_ROOT_SELECTOR) ?? undefined;

  return {
    anchorEl,
    dismiss,
    getTriggerProps,
    isOpen,
    items,
    onOptionClick,
    panelContainer,
  };
}

export default useAddModuleMenuButton;
